import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "@/i18n/LangRouting";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { useI18n } from "@/i18n/I18nContext";
import { useExhibition } from "@/visitor/ExhibitionShell";
import { Logo, LangButton } from "@/visitor/Header";
import { usePageTitle } from "@/lib/pageTitle";
import { giftsOf, giftName, discountOf } from "@/lib/gifts";

export const Footer = ({ t }) => (
  <footer className="flex flex-wrap gap-6 text-xs text-[var(--muted)] tracking-wider" data-testid="signup-footer">
    <Link to="/legal/impressum" className="hover:text-white transition-colors" data-testid="footer-impressum">{t("impressum")}</Link>
    <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-datenschutz">{t("datenschutz")}</Link>
    <button type="button" onClick={() => window.dispatchEvent(new Event("bg-open-cookie-settings"))} className="hover:text-white transition-colors" data-testid="footer-cookie-settings">{t("cookie_settings")}</button>
  </footer>
);

function GiftOption({ value, current, onChange, title, sub, right, testId }) {
  const checked = current === value;
  return (
    <button type="button" onClick={() => onChange(value)} data-testid={testId} role="radio" aria-checked={checked}
      className={`w-full flex items-center gap-4 border px-4 py-4 text-left transition-colors ${checked ? "border-[var(--accent)]" : "border-[var(--line)] hover:border-[#555]"}`}>
      <span className="radio-dot" data-checked={checked} />
      <span className="flex-1 min-w-0">
        <span className="font-semibold block">{title}</span>
        <span className="block text-xs text-[var(--muted)] mt-0.5">{sub}</span>
      </span>
      {right}
    </button>
  );
}

function PrivacyLine({ t, nogift }) {
  const [before, after] = t(nogift ? "privacy_line_nogift" : "privacy_line").split("{privacy}");
  return <p className="text-xs text-[var(--muted)] pt-3" data-testid="signup-privacy-line">{before}<Link to="/legal/privacy" className="underline text-white hover:text-accent" data-testid="signup-privacy-link">{t("privacy_policy")}</Link>{after}</p>;
}

function ExhibitionSelect({ t, options, value, onChange }) {
  return (
    <div>
      <label className="label" htmlFor="exhibition">{t("signup_which_exhibition")}</label>
      <div className="relative">
        <select id="exhibition" className="field" value={value} onChange={(e) => onChange(e.target.value)} required data-testid="signup-exhibition">
          <option value="">{t("signup_choose_exhibition")}</option>
          {options.map((e) => <option key={e.slug} value={e.slug}>{e.subtitle ? `${e.name} – ${e.subtitle}` : e.name}</option>)}
        </select>
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none">▾</span>
      </div>
    </div>
  );
}

const registerVisitorLocal = (data) => {
  localStorage.setItem("bg_visitor_token", data.token);
  localStorage.setItem("bg_visitor", JSON.stringify({ first_name: data.first_name, last_name: data.last_name, gift: data.gift, code: data.code }));
};

export default function SignupPage({ general = false }) {
  const shell = useExhibition();
  const { legalText, t, prefix, lang } = useI18n();
  const hasGiftTerms = !!legalText("gift_terms");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [options, setOptions] = useState([]);
  const [chosen, setChosen] = useState("");
  useEffect(() => {
    if (!general) return;
    api.get("/public/exhibitions").then((r) => { const l = r.data.filter((e) => e.show_on_home); setOptions(l); if (l.length === 1) setChosen(l[0].slug); }).catch(() => {});
  }, [general]);
  const slug = general ? chosen : shell.slug;
  const exhibition = general ? options.find((e) => e.slug === chosen) || null : shell.exhibition;
  const gifts = giftsOf(exhibition);
  const giftLabel = giftName(exhibition, lang, t);
  const brandExhibition = general ? null : shell.exhibition;
  const visitorToken = general ? localStorage.getItem("bg_visitor_token") : shell.visitorToken;
  const next = params.get("next") || `${prefix}/e/${slug}`;
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", country: "" });
  const [gift, setGift] = useState("discount");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const effectiveGift = !gifts.enabled ? "none" : (gift === "discount" && gifts.discount) || (gift === "poster" && gifts.instore) ? gift : gifts.discount ? "discount" : "poster";
  const sfx = gifts.enabled ? "" : "_nogift";

  const submit = async (e) => {
    e.preventDefault();
    if (!slug) { toast.error(t("signup_exhibition_required")); return; }
    setBusy(true);
    try {
      const token = visitorToken && !visitorToken.startsWith("skip-") ? visitorToken : undefined;
      const { data } = await api.post("/public/signup", { exhibition_slug: slug, ...form, gift: effectiveGift, token, language: lang });
      (general ? registerVisitorLocal : shell.registerVisitor)(data);
      if (data.gift === "poster") navigate(`${prefix}/e/${slug}/voucher?next=${encodeURIComponent(next)}`, { replace: true });
      else navigate(`${next}${next.includes("?") ? "&" : "?"}welcome=${data.gift === "none" ? "newsletter" : "discount"}`, { replace: true });
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const skip = () => {
    if (general) { navigate(slug ? next : prefix, { replace: true }); return; }
    shell.skipSignup(); navigate(next, { replace: true });
  };
  usePageTitle(general ? t("site_name") : null);
  const kicker = exhibition ? `${exhibition.name} · ${t("free_audio_guide")}` : t("free_audio_guide");

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2" data-testid="signup-page">
      <aside className="hidden lg:flex relative grain flex-col justify-between p-12 border-r border-[var(--line)]">
        <Link to="/"><Logo exhibition={brandExhibition} /></Link>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--muted)]">{kicker}</p>
          <h2 className="font-display text-5xl xl:text-6xl uppercase leading-[0.95] mt-4">{t(`signup_title${sfx}`)}</h2>
          <p className="text-[var(--muted)] mt-5 max-w-sm">{t(`signup_subtitle${sfx}`)}</p>
        </div>
        <Footer t={t} />
      </aside>
      <main className="flex flex-col min-h-screen px-6 py-5 sm:px-10 lg:px-16 lg:py-12 max-w-xl lg:max-w-none mx-auto w-full">
        <div className="flex items-center justify-between lg:justify-end">
          <Link to="/" className="lg:hidden"><Logo exhibition={brandExhibition} sub={kicker} /></Link>
          <LangButton />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95] mt-10" data-testid="signup-title">{t(`signup_title${sfx}`)}</h1>
        <p className="text-sm text-[var(--muted)] mt-3 lg:hidden">{t(`signup_subtitle${sfx}`)}</p>
        <form className="mt-8 space-y-4 fade-up" onSubmit={submit} data-testid="signup-form">
          {general && <ExhibitionSelect t={t} options={options} value={chosen} onChange={setChosen} />}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label" htmlFor="first_name">{t("first_name")}</label><input id="first_name" className="field" value={form.first_name} onChange={set("first_name")} data-testid="signup-first-name" autoComplete="given-name" /></div>
            <div><label className="label" htmlFor="last_name">{t("last_name")}</label><input id="last_name" className="field" value={form.last_name} onChange={set("last_name")} data-testid="signup-last-name" autoComplete="family-name" /></div>
          </div>
          <div><label className="label" htmlFor="email">{t("email")}</label><input id="email" className="field" type="email" value={form.email} onChange={set("email")} data-testid="signup-email" autoComplete="email" /></div>
          <div>
            <label className="label" htmlFor="country">{t("country")}</label>
            <div className="relative">
              <select id="country" className="field" value={form.country} onChange={set("country")} data-testid="signup-country">
                <option value="">—</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none">▾</span>
            </div>
          </div>
          {gifts.enabled && (
          <div className="pt-3 space-y-2" role="radiogroup" aria-label={t("choose_gift")} data-testid="signup-gift-choice">
            <div className="flex items-baseline justify-between">
              <p className="label">{t("choose_gift")}</p>
              {hasGiftTerms && <Link to="/legal/gift_terms" className="text-[0.65rem] tracking-wider uppercase text-[var(--muted)] underline hover:text-white" data-testid="signup-gift-terms-link">{t("gift_terms")}</Link>}
            </div>
            {gifts.discount && <GiftOption value="discount" current={effectiveGift} onChange={setGift} title={t("gift_discount_title", { discount: discountOf(exhibition) })} sub={t("gift_discount_sub")} testId="gift-discount" right={<span className="font-display text-2xl text-accent flex-none">-{discountOf(exhibition)}%</span>} />}
            {gifts.instore && <GiftOption value="poster" current={effectiveGift} onChange={setGift} title={giftLabel} sub={t("gift_poster_sub")} testId="gift-poster" />}
          </div>
          )}
          <PrivacyLine t={t} nogift={!gifts.enabled} />
          <button type="submit" disabled={busy} className="btn-accent w-full py-4 mt-2 flex items-center justify-center gap-3" data-testid="signup-submit-button">{t("start_guide")} <ArrowRight size={18} /></button>
          <button type="button" disabled={busy} onClick={skip} className={`w-full py-3 text-sm transition-colors ${gifts.enabled ? "text-[var(--muted)] hover:text-white" : "underline text-white hover:text-accent"}`} data-testid="signup-skip-button">{t(gifts.enabled ? "continue_without_gift" : "continue_without_signup")}</button>
        </form>
        <div className="mt-auto pt-10 lg:hidden"><Footer t={t} /></div>
      </main>
    </div>
  );
}
